import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tray, ArrowRight, Clock, User as UserIcon, FileText } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

/**
 * Pending Reviews — Main staff (and admin/owner) sees a queue of every
 * sub_staff handoff that's awaiting their final approval.
 *
 * Each row shows: which file, which sub_staff, how many rows, total ₹,
 * earliest/latest submitted-for-review timestamps. Click "Review →" to
 * open the workspace where the rows can be Approve-and-locked or Rejected
 * back to the sub_staff with a note.
 *
 * Backend: GET /booking-execution/pending-reviews
 */
export default function PendingReviewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/booking-reviews/pending');
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalRows = items.reduce((s, i) => s + (i.rows_count || 0), 0);
  const totalAmt = items.reduce((s, i) => s + (i.total_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tray size={24} weight="duotone" className="text-amber-600" /> Pending Reviews
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Aapke sub-staff ne jo bookings submit ki hain, yahan se review aur approve karein
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded font-bold border border-amber-300">
              {totalRows} rows · ₹{Math.round(totalAmt).toLocaleString('en-IN')}
            </span>
          </div>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>Sub-Staff</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead className="text-right">Rows</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Sent For Review</TableHead>
              <TableHead className="text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow> :
             items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                  <Tray size={36} weight="duotone" className="mx-auto text-gray-300 mb-2" />
                  <div className="font-medium">Koi pending review nahi.</div>
                  <div className="text-xs text-gray-400 mt-1">Sub-staff jab apne groups submit karenge, yahan dikhenge.</div>
                </TableCell>
              </TableRow>
             ) :
             items.map((g) => (
              <TableRow key={`${g.file_id}-${g.submitter_id}`} data-testid={`pending-${g.file_id}-${g.submitter_id}`}>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-1.5">
                    <UserIcon size={14} className="text-blue-600" />
                    <span className="font-medium">{g.submitter_name || '—'}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="flex items-center gap-1">
                    <FileText size={12} className="text-gray-400 shrink-0" />
                    <span className="truncate max-w-[260px]" title={g.file_name}>{g.file_name || '—'}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-gray-600">{g.distributor_name || '—'}</TableCell>
                <TableCell className="text-right text-sm font-bold text-amber-700">{g.rows_count}</TableCell>
                <TableCell className="text-right text-sm font-bold text-emerald-700">
                  ₹{Math.round(g.total_amount).toLocaleString('en-IN')}
                </TableCell>
                <TableCell className="text-xs text-gray-500">
                  <Clock size={11} className="inline mr-0.5" />
                  {formatDateTime(g.earliest)}
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/booking-workspace/${g.file_id}`)}
                    className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`review-${g.file_id}-${g.submitter_id}`}
                  >
                    Review <ArrowRight size={12} className="ml-1" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
